import * as React from 'react';

import { useToast } from '@longline/aqua-ui/services/Toast/useToast';

import { IUser } from '../../api/models/IUser';
import { TMode, UsersContext } from './IUsersContext';
import { UserApi } from '../../api/services/UserApi';
import { TResourceState } from '../../types/TResourceState';

import { UserList } from './UserList';
import { ViewUser } from './ViewUser';
import { AddUser } from './AddUser';
import { EditUser } from './EditUser';

const UsersManager = () => {
  // UserManager operating mode:
  const [mode, setMode] = React.useState<TMode>('list');
  // User proxies loaded from back end:
  const [users, setUsers] = React.useState<IUser[]>([]);
  const [usersReady, setUsersReady] = React.useState<TResourceState>('loading');
  // Current user:
  const [user, setUser] = React.useState<IUser | null>(null);
  const [userReady, setUserReady] = React.useState<TResourceState>('loading');
  const [tab, setTab] = React.useState<'data'|'systems'>('data');

  const toastProvider = useToast();

  React.useEffect(() => {
    loadUsers();
  }, []);

  /**
   * Loads list of users from the back end.
   */
  const loadUsers = () => {
    setUsers([]);
    setUsersReady('loading');
    UserApi.list()
    .then((res) => {
      setUsers(res);
      setUsersReady('ready');
    })
    .catch(() => {
      setUsersReady('failed');
    });
  }

  /**
   * Loads a single user from back end.
   * @param uuid User UUID
   */
  const loadUser = (uuid: string): void => {
    setUserReady('loading');
    UserApi.get(uuid)
    .then(data => {
      setUser({...user, ...data});
      setUserReady('ready');
    })
    .catch(err => {
      setUserReady('failed');
      toastProvider.toast("Something went wrong while loading the user. Please try again.", { dismissable: true });     
    });
  }

  const parseError = (error: any): string => {
    if(error?.code == 'ERR_CANCELED') return "Server timeout."
    const status = error?.response?.status;
    switch(status) {
      case 400: return "Keycloak could not store this user.";
      case 403: return "This operation is not permitted.";
      case 409: return "A user with this email already exists.";
      case 422: return "The form content could not be validated.";
      default: return "Something went wrong while creating the user. Please try again.";
    }
  }    

  /**
   * Creates a new user.
   */
  const createUser = async (user: IUser, signal: AbortSignal): Promise<TResourceState> => {
    await UserApi.create(user, signal)
    .then((createdUser: IUser) => {
      setUsers([...users.filter(u => u.uuid != createdUser.uuid), createdUser ]);
      toastProvider.toast("User created successfully.", { dismissable: true });
      setMode('list');
    })
    .catch(error => {
      toastProvider.toast(parseError(error), { dismissable: true });
      return 'failed';
    });
    return 'ready';
  }

  const updateUser = async (user: IUser, signal: AbortSignal): Promise<TResourceState> => {
    await UserApi.update(user, signal)
    .then((updatedUser: IUser) => {
      setUser({...user, ...updatedUser});
      setUsers([...users.filter(u => u.uuid != updatedUser.uuid), updatedUser ]);
      setMode('view');
      toastProvider.toast("User updated successfully.", { dismissable: true });
    })
    .catch(error => {
      toastProvider.toast(parseError(error), { dismissable: true });
      return 'failed';
    });
    return 'ready';
  }
  
  const deleteUser = () => {
    if (!user) return;
    const userUuid = user.uuid;
    setUserReady('loading');
    UserApi.delete(userUuid)
    .then((_) => {
      setUser(null);
      setUsers(prevUsers => prevUsers.filter(u => u.uuid !== userUuid));
      setUserReady('ready');
      setMode('list');
      toastProvider.toast("User deleted successfully.", { dismissable: true });
    })
    .catch(err => {
      setUserReady('failed');
      toastProvider.toast("Failed to delete user.", { dismissable: true });
    });
  }

  const renderMode = () => {
    switch(mode) {
      case 'list': return <UserList/>;
      case 'view': return <ViewUser/>;
      case 'add': return <AddUser/>;
      case 'edit': return <EditUser/>;
      default: return "Unimplemented mode"
    }
  }

  return (
    <UsersContext.Provider value={{
      mode, setMode,
      tab, setTab,
      users, setUsers, usersReady,
      loadUsers,
      user, setUser, userReady, setUserReady,
      loadUser,
      updateUser,
      createUser,
      deleteUser,
    }}>
      {renderMode()}
    </UsersContext.Provider>
  )
}

export { UsersManager }
